import { useState } from "react";
import { motion } from "framer-motion";
import { Expand, ArrowUpRight } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { SectionHead } from "@/components/site/SectionHead";
import { useQuote } from "@/components/site/Cta";
import { GALLERY } from "@/content/site";

const SPAN = { wide: "md:col-span-2", tall: "md:row-span-2" };

const Tile = ({ g, i, onOpen }) => (
  <motion.button
    type="button" onClick={() => onOpen(i)} data-testid={`gallery-item-${i + 1}`}
    initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }}
    transition={{ duration: 0.9, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
    className={`group relative overflow-hidden rounded-2xl border border-white/10 bg-[#0d0f12] text-left md:aspect-auto ${g.span === "tall" ? "aspect-[3/4]" : "aspect-[4/3]"} ${SPAN[g.span] || ""}`}
  >
    <img src={g.src} alt={g.alt} loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105" />
    <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-transparent opacity-90 transition-opacity duration-500 group-hover:opacity-100" />
    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-3 p-5">
      <div>
        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-amber-400">{String(i + 1).padStart(2, "0")} · Texas</p>
        <p className="mt-1.5 text-sm font-semibold text-white">{g.caption}</p>
      </div>
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-white/20 bg-black/40 backdrop-blur transition-[background-color,border-color] duration-300 group-hover:border-amber-400 group-hover:bg-amber-400/20">
        <Expand className="h-4 w-4 text-white" />
      </span>
    </div>
  </motion.button>
);

const CtaTile = () => {
  const quote = useQuote();
  return (
    <button type="button" onClick={() => quote("Solar Installation")} data-testid="gallery-cta-tile"
      className="group relative flex min-h-[220px] flex-col justify-between overflow-hidden rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-400/15 to-transparent p-7 text-left">
      <div className="pointer-events-none absolute -bottom-16 -right-16 h-48 w-48 rounded-full bg-amber-500/25 blur-3xl sun-breathe" />
      <p className="eyebrow relative">Your Project</p>
      <div className="relative">
        <p className="font-display text-xl uppercase leading-tight text-white">Your home could be next.</p>
        <span className="mt-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-amber-400">
          Get a Free Quote <ArrowUpRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1 group-hover:-translate-y-1" />
        </span>
      </div>
    </button>
  );
};

export const Gallery = () => {
  const [open, setOpen] = useState(null);
  const g = open === null ? null : GALLERY[open];
  return (
    <section id="gallery" data-testid="gallery-section" className="relative bg-[#0b0c0e] py-24 sm:py-32">
      <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12">
        <SectionHead eyebrow="Project Gallery" lines={["Real work.", "Real Texas homes."]}
          sub="A look at recent solar projects completed by the Solar Energy Doc team." />
        <div className="mt-16 grid grid-cols-1 gap-4 md:grid-cols-3 md:auto-rows-[280px] md:grid-flow-row-dense">
          {GALLERY.map((item, i) => <Tile key={item.src} g={item} i={i} onOpen={setOpen} />)}
          <CtaTile />
        </div>
      </div>
      <Dialog open={open !== null} onOpenChange={(v) => { if (!v) setOpen(null); }}>
        <DialogContent data-testid="gallery-lightbox" className="max-w-5xl border-white/10 bg-[#08090a] p-3 sm:p-4 text-white">
          <DialogTitle className="sr-only">{g?.caption}</DialogTitle>
          {g && <img src={g.src} alt={g.alt} className="max-h-[80vh] w-full rounded-lg object-contain" />}
          {g && <p className="px-1 pb-1 text-sm text-zinc-300">{g.caption}</p>}
        </DialogContent>
      </Dialog>
    </section>
  );
};
